import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Partner, PartnerType } from '../../models/partner';
import { Connection, CreateConnectionRequest } from '../../models/connection';
import { CreateTenantRequest, Currency, FeatureFlagDTO, featureFlagLabel, QuotaUnit, QUOTA_UNIT_LABELS, SmsCredit, SmsCreditLedgerEntry, StorageUsage, UpdateFeatureFlagRequest, FeatureFlag } from '../../models/tenant';
import { AdminUserCreateRequest, AdminUserDTO, InvitationDTO, assignableRolesFor, UserRole } from '../../models/user';
import {
  formatAbsolute, formatRelative, lastActiveTooltip,
  userRoleClass, userRoleLabel, userStatusClass, userStatusLabel
} from '../../utils/user-display';
import { Case, CasesQueryParams } from '../../models/case';
import { Shipment, ShipmentsQueryParams } from '../../models/shipment';
import { PartnersService } from '../../../services/partners.service';
import { CasesService } from '../../../services/cases.service';
import { forkJoin } from 'rxjs';

import { Subscription } from '../../models/subscription';
import { SubscriptionService } from '../../../services/subscription.service';
@Component({
  selector: 'app-partner-details',
  templateUrl: './partner-details.component.html',
  styleUrls: ['./partner-details.component.scss']
})
export class PartnerDetailsComponent implements OnInit, OnDestroy {
  partner: Partner | null = null;
  connections: Connection[] = [];
  users: AdminUserDTO[] = [];
  // Keycloak groups are /partners/{tenantId}, so only tenants can have users at all.
  usersNotTenant = false;
  cases: Case[] = [];
  shipments: Shipment[] = [];
  loading = false;
  connectionsLoading = false;
  usersLoading = false;
  casesLoading = false;
  shipmentsLoading = false;
  error: string | null = null;
  connectionsError: string | null = null;
  usersError: string | null = null;
  casesError: string | null = null;
  shipmentsError: string | null = null;
  partnerId: string | null = null;
  
  // Logo handling
  logoBlobUrl: string | null = null;
  logoLoading = false;
  logoError: string | null = null;
  
  // Tab management
  activeTab: 'basic' | 'connections' | 'users' | 'cases' = 'basic';
  
  // Cases pagination
  /** The case awaiting confirmation, or null when the dialog is closed. */
  pendingDelete: Case | null = null;
  deletingCase = false;

  casesPage = 0;
  casesSize = 10;
  casesTotalElements = 0;
  casesTotalPages = 0;
  
  // Shipments pagination
  shipmentsPage = 0;
  shipmentsSize = 10;
  shipmentsTotalElements = 0;
  shipmentsTotalPages = 0;
  
  // Add connection form
  showAddConnectionForm = false;
  addConnectionLoading = false;
  addConnectionError: string | null = null;
  newConnection = {
    toConnectId: ''
  };
  
  // Searchable partner selection
  filteredPartners: Partner[] = [];
  selectedPartner: Partner | null = null;
  partnersLoaded = false;
  loadingPartners = false;
  currentSearchTerm: string = '';
  tenantCreationLoading = false;
  selectedTenantCurrency: Currency = Currency.EUR;
  readonly availableCurrencies = Object.values(Currency);
  readonly Currency = Currency;

  // Add user form (tenant-only, for the same reason users themselves are)
  showAddUserForm = false;
  addUserLoading = false;
  addUserError: string | null = null;
  newUser: { firstName: string; lastName: string; email: string; role: UserRole | '' } = {
    firstName: '',
    lastName: '',
    email: '',
    role: ''
  };
  // Held only until dismissed: the backend generates this once and cannot return it again.
  createdUserCredentials: { email: string; password: string } | null = null;
  /**
   * Set when the email entered belongs to an account that already exists. The form stays put and
   * asks, because adding somebody to a tenant puts a real person into somebody's organisation and
   * a taken email is more often a typo than the person meant.
   */
  existingAccountEmail: string | null = null;
  /** The email of an account that was added rather than created, shown in place of credentials. */
  addedExistingAccount: string | null = null;

  /** Only the roles that fit this partner's type; the backend refuses any other with a 400. */
  get assignableRoles(): UserRole[] {
    return assignableRolesFor(this.partner?.type);
  }

  // Feature flags (tenant-only)
  featureFlags: FeatureFlagDTO[] = [];
  subscription: Subscription | null = null;

  storage: StorageUsage | null = null;
  smsCredit: SmsCredit | null = null;
  smsLedger: SmsCreditLedgerEntry[] = [];
  smsCreditAmount: number | null = null;
  smsCreditReason: 'TOPUP' | 'ADJUST' = 'TOPUP';
  smsCreditNote = '';
  smsCreditSaving = false;
  // Kept until a change succeeds, so sending the same one again after an error counts once.
  private smsCreditRequestId = crypto.randomUUID();
  flagEdits: Record<string, boolean> = {};
  /**
   * The monthly allowance next to each metered flag. Seeded as a string, but the input is
   * type="number", so once someone types in it ngModel writes back a number, or null when it is
   * emptied. Read it through typedLimit(), never directly. An emptied input is meaningful here: it
   * means "put this tenant back on the free plan".
   */
  limitEdits: Record<string, string | number | null> = {};
  /**
   * Boxes holding text the browser could not parse as a number ("1,000", "5e", "-"). ngModel
   * reports those as null, exactly like an emptied box, so without this a typo would read as
   * "free plan" and quietly drop the tenant's negotiated allowance.
   */
  limitUnparseable: Record<string, boolean> = {};
  flagsLoading = false;
  flagsSaving = false;
  flagsError: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private partnersService: PartnersService,
    private casesService: CasesService,
    private snackBar: MatSnackBar,
    private subscriptionService: SubscriptionService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.partnerId = params.get('id');
      // The component is reused across /partners/:id links; a half-filled user form (name, email,
      // a role only the previous partner's type allows) must never carry over to another tenant.
      this.showAddUserForm = false;
      this.resetNewUserForm();
      if (this.partnerId) {
        this.loadPartnerDetails(this.partnerId);
        this.loadPartnerConnections(this.partnerId);
        // Users are loaded from loadPartnerDetails — we need isTenant first.
        // Partners will be loaded when the search field is focused
      } else {
        this.error = 'No partner ID provided';
      }
    });
  }

  loadPartnerDetails(id: string): void {
    this.loading = true;
    this.error = null;

    // Use the dedicated API endpoint to get partner by ID
    this.partnersService.getPartnerById(id).subscribe({
      next: (partner: Partner) => {
        this.partner = partner;
        this.loading = false;
        
        // Load logo if available
        if (partner.logoAttachment?.url) {
          this.loadPartnerLogo(partner.logoAttachment.url);
        }

        // Load feature flags and users for tenants (tenantId === partnerId)
        if (partner.isTenant && partner.id != null) {
          this.loadFeatureFlags(partner.id);
          this.loadSmsCredit(partner.id);
          this.partnersService.getStorage(partner.id).subscribe({
            next: storage => this.storage = storage,
            error: () => this.storage = null
          });
          this.subscriptionService.getTenantSubscription(partner.id).subscribe({
            next: subscription => this.subscription = subscription,
            error: () => this.subscription = null
          });
          this.loadPartnerUsers(partner.id);
        } else {
          // Users live in the Keycloak group /partners/{tenantId}. A non-tenant partner has no
          // such group, so the endpoint would just 200 with an empty list and no explanation.
          this.usersNotTenant = true;
          this.users = [];
          this.usersLoading = false;
        }

        // Load cases or shipments after partner details are loaded
        if (partner.type === PartnerType.COURIER) {
          this.loadPartnerShipments();
        } else {
          this.loadPartnerCases();
        }
      },
      error: (error) => {
        console.error('Error loading partner details:', error);
        if (error.status === 404) {
          this.error = 'Partner not found';
        } else if (error.status === 401) {
          this.error = 'You are not authorized to view this partner';
        } else if (error.status === 403) {
          this.error = 'Access denied to view this partner';
        } else {
          this.error = 'Failed to load partner details. Please try again.';
        }
        this.loading = false;
      }
    });
  }

  loadPartnerLogo(imageUrl: string): void {
    this.logoLoading = true;
    this.logoError = null;
    
    this.partnersService.downloadImage(imageUrl).subscribe({
      next: (blob: Blob) => {
        // Create blob URL for the image
        this.logoBlobUrl = URL.createObjectURL(blob);
        this.logoLoading = false;
      },
      error: (error) => {
        console.error('Error loading partner logo:', error);
        this.logoError = 'Failed to load partner logo';
        this.logoLoading = false;
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/partners']);
  }

  editPartner(): void {
    if (this.partnerId) {
      this.router.navigate(['/partners', this.partnerId, 'edit']);
    }
  }

  /** Onboarding imports run against a tenant, so this is offered only once one exists. */
  openImport(): void {
    if (this.partnerId) {
      this.router.navigate(['/partners', this.partnerId, 'import']);
    }
  }

  createTenant(): void {
    if (!this.partnerId) {
      this.snackBar.open('Partner ID not found', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.tenantCreationLoading = true;

    const createTenantData: CreateTenantRequest = {
      primaryPartnerId: parseInt(this.partnerId, 10),
      currency: this.selectedTenantCurrency
    };

    this.partnersService.createTenant(createTenantData).subscribe({
      next: (tenant) => {
        this.tenantCreationLoading = false;
        this.loadPartnerDetails(this.partnerId!);
        this.snackBar.open(
          `Tenant #${tenant.id} created successfully`,
          'Close',
          {
            duration: 4000,
            panelClass: ['success-snackbar']
          }
        );
      },
      error: (error) => {
        console.error('Error creating tenant:', error);
        this.tenantCreationLoading = false;

        const message = error.error?.message || 'Failed to create tenant. Please try again.';
        this.snackBar.open(message, 'Close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  /** Storage has a limit but no switch: enforcement ignores it, so offering one would mislead. */
  hasSwitch(flag: FeatureFlagDTO): boolean {
    return flag.name !== FeatureFlag.FEATURE_STORAGE;
  }

  formatGb(bytes: number): string {
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
  }

  // ---- SMS credits ----

  loadSmsCredit(tenantId: number): void {
    this.partnersService.getSmsCredit(tenantId).subscribe({
      next: credit => this.smsCredit = credit,
      error: () => this.smsCredit = null
    });
    this.partnersService.getSmsCreditLedger(tenantId).subscribe({
      next: ledger => this.smsLedger = ledger.items,
      error: () => this.smsLedger = []
    });
  }

  canChangeSmsCredit(): boolean {
    const amount = this.smsCreditAmount;
    return amount != null && Number.isInteger(amount) && amount !== 0
      && Math.abs(amount) <= 1000000 && (this.smsCreditReason === 'ADJUST' || amount > 0);
  }

  changeSmsCredit(): void {
    if (this.partner?.id == null || !this.canChangeSmsCredit() || this.smsCreditSaving) {
      return;
    }
    const tenantId = this.partner.id;
    this.smsCreditSaving = true;
    this.partnersService.changeSmsCredit(tenantId, {
      amount: this.smsCreditAmount as number,
      reason: this.smsCreditReason,
      note: this.smsCreditNote.trim() || null,
      requestId: this.smsCreditRequestId
    }).subscribe({
      next: () => {
        this.smsCreditSaving = false;
        this.smsCreditAmount = null;
        this.smsCreditNote = '';
        this.smsCreditRequestId = crypto.randomUUID();
        this.loadSmsCredit(tenantId);
        this.snackBar.open('SMS credit updated', 'Close', { duration: 3000 });
      },
      error: () => {
        this.smsCreditSaving = false;
        this.snackBar.open('Could not update SMS credit', 'Close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  // ---- Feature flags ----

  loadFeatureFlags(tenantId: number): void {
    this.flagsLoading = true;
    this.flagsError = null;

    this.partnersService.getTenantFeatureFlags(tenantId).subscribe({
      next: (response) => {
        this.featureFlags = response.items || [];
        this.flagEdits = {};
        this.limitEdits = {};
        this.limitUnparseable = {};
        this.featureFlags.forEach(flag => {
          this.flagEdits[flag.name] = flag.enabled;
          this.limitEdits[flag.name] = flag.monthlyLimit == null ? '' : String(flag.monthlyLimit);
        });
        this.flagsLoading = false;
      },
      error: (error) => {
        console.error('Error loading feature flags:', error);
        this.flagsError = 'Failed to load feature flags';
        this.flagsLoading = false;
      }
    });
  }

  flagLabel(name: string): string {
    return featureFlagLabel(name);
  }

  /** Whether a flag costs money per use, and therefore has an allowance to set. */
  isMetered(flag: FeatureFlagDTO): boolean {
    return flag.unit != null && flag.unit !== QuotaUnit.NONE;
  }

  unitLabel(flag: FeatureFlagDTO): string {
    return QUOTA_UNIT_LABELS[flag.unit] || '';
  }

  /** What the input shows when it is empty: the free-plan figure this tenant falls back to. */
  freePlanPlaceholder(flag: FeatureFlagDTO): string {
    return flag.effectiveMonthlyLimit == null ? '' : `${flag.effectiveMonthlyLimit} (free plan)`;
  }

  private limitChanged(flag: FeatureFlagDTO): boolean {
    if (!this.isMetered(flag)) {
      return false;
    }
    const stored = flag.monthlyLimit == null ? '' : String(flag.monthlyLimit);
    return this.typedLimit(flag) !== stored;
  }

  /** What is in the allowance box, as trimmed text; '' when it is empty. */
  private typedLimit(flag: FeatureFlagDTO): string {
    return String(this.limitEdits[flag.name] ?? '').trim();
  }

  hasFlagChanges(): boolean {
    return this.featureFlags.some(flag =>
      this.flagEdits[flag.name] !== flag.enabled || this.limitChanged(flag));
  }

  /** Records whether the browser could parse what was typed; see limitUnparseable. */
  onLimitInput(flag: FeatureFlagDTO, event: Event): void {
    this.limitUnparseable[flag.name] = (event.target as HTMLInputElement).validity.badInput;
  }

  /** Empty (the free plan) or a whole number the backend's Integer column can hold. */
  limitValid(flag: FeatureFlagDTO): boolean {
    if (!this.isMetered(flag)) {
      return true;
    }
    if (this.limitUnparseable[flag.name]) {
      return false;
    }
    const typed = this.typedLimit(flag);
    if (typed === '') {
      return true;
    }
    const value = Number(typed);
    return Number.isInteger(value) && value >= 0 && value <= 2147483647;
  }

  canSaveFlags(): boolean {
    return this.hasFlagChanges() && this.featureFlags.every(flag => this.limitValid(flag));
  }

  saveFeatureFlags(): void {
    if (!this.partner?.isTenant || this.partner.id == null) {
      return;
    }

    const tenantId = this.partner.id;
    const changed = this.featureFlags.filter(flag =>
      this.flagEdits[flag.name] !== flag.enabled || this.limitChanged(flag));

    if (changed.length === 0 || !this.canSaveFlags()) {
      return;
    }

    this.flagsSaving = true;

    const requests = changed.map(flag => {
      // The limit always goes with the flag, so an emptied input reaches the backend as null and
      // puts the tenant back on the free plan rather than silently leaving the old figure behind.
      const typed = this.typedLimit(flag);
      const body: UpdateFeatureFlagRequest = {
        featureFlag: flag.name,
        enabled: this.flagEdits[flag.name],
        monthlyLimit: this.isMetered(flag) && typed !== '' ? Number(typed) : null
      };
      return this.partnersService.updateTenantFeatureFlag(tenantId, body);
    });

    forkJoin(requests).subscribe({
      next: (updated) => {
        // Apply the updated states onto the current list
        updated.forEach(u => {
          const existing = this.featureFlags.find(f => f.name === u.name);
          if (existing) {
            existing.enabled = u.enabled;
            existing.monthlyLimit = u.monthlyLimit;
            existing.effectiveMonthlyLimit = u.effectiveMonthlyLimit;
            this.flagEdits[u.name] = u.enabled;
            this.limitEdits[u.name] = u.monthlyLimit == null ? '' : String(u.monthlyLimit);
          }
        });
        this.flagsSaving = false;
        this.snackBar.open('Feature flags updated', 'Close', {
          duration: 4000,
          panelClass: ['success-snackbar']
        });
      },
      error: (error) => {
        console.error('Error updating feature flags:', error);
        this.flagsSaving = false;
        const message = error.error?.message || 'Failed to update feature flags. Please try again.';
        this.snackBar.open(message, 'Close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  getFormattedAddress(): string {
    if (!this.partner?.contact?.address) {
      return 'No address provided';
    }
    const addr = this.partner.contact.address;
    const parts = [
      addr.street,
      addr.city,
      addr.state,
      addr.zip
    ].filter(part => part && part.trim());
    
    return parts.length > 0 ? parts.join(', ') : 'No address provided';
  }

  getContactEmail(): string {
    return this.partner?.contact?.email || 'N/A';
  }

  getContactPhone(): string {
    return this.partner?.contact?.phoneNumber || 'N/A';
  }

  getFaxNo(): string {
    return this.partner?.faxNo || 'N/A';
  }

  getBankAccounts(): any[] {
    return this.partner?.bankAccounts || [];
  }

  getAddressStreet(): string {
    return this.partner?.contact?.address?.street || 'N/A';
  }

  getAddressCity(): string {
    return this.partner?.contact?.address?.city || 'N/A';
  }

  getAddressState(): string {
    return this.partner?.contact?.address?.state || 'N/A';
  }

  getAddressZip(): string {
    return this.partner?.contact?.address?.zip || 'N/A';
  }

  getPartnerTypeLabel(): string {
    switch (this.partner?.type) {
      case PartnerType.DENTIST: return 'Dentist';
      case PartnerType.TECH: return 'Tech';
      case PartnerType.COURIER: return 'Courier';
      case PartnerType.SUPPLIER: return 'Supplier';
      default: return 'Unknown';
    }
  }

  loadPartnerConnections(partnerId: string): void {
    this.connectionsLoading = true;
    this.connectionsError = null;

    this.partnersService.getPartnerConnections(partnerId).subscribe({
      next: (response: any) => {
        // Handle PaginatedCollection<Partner> response
        let partners: Partner[] = [];
        
        if (response && response.content && Array.isArray(response.content)) {
          partners = response.content;
        } else if (Array.isArray(response)) {
          partners = response;
        } else if (response && Array.isArray(response.data)) {
          partners = response.data;
        }
        
        // Transform Partner objects to Connection objects for display
        this.connections = partners.map((partner: Partner) => ({
          id: partner.id,
          connectedPartner: partner,
          status: 'active', // Default status since it's not in the Partner object
          createdAt: new Date().toISOString() // Default date since it's not in the Partner object
        }));
        
        this.connectionsLoading = false;
      },
      error: (error) => {
        console.error('Error loading partner connections:', error);
        this.connectionsError = 'Failed to load partner connections';
        this.connectionsLoading = false;
      }
    });
  }

  loadAvailablePartners(searchTerm?: string): void {
    if (this.loadingPartners) {
      return; // Already loading
    }

    this.loadingPartners = true;
    this.currentSearchTerm = searchTerm || '';
    
    // Load first 10 partners with optional search term
    this.partnersService.getPartners({ page: 0, size: 10, sort: ['contact.name,asc'], searchTerm }).subscribe({
      next: (response: any) => {
        let partnersList: Partner[] = [];
        
        // Handle different response formats
        if (response && Array.isArray(response.data)) {
          partnersList = response.data;
        } else if (Array.isArray(response)) {
          partnersList = response;
        } else if (response && response.content && Array.isArray(response.content)) {
          partnersList = response.content;
        }
        
        // Filter out the current partner from available partners
        const filtered = partnersList.filter(p => p.id?.toString() !== this.partnerId);
        this.filteredPartners = filtered;
        this.partnersLoaded = true;
        this.loadingPartners = false;
      },
      error: (error) => {
        console.error('Error loading available partners:', error);
        this.loadingPartners = false;
        this.filteredPartners = [];
      }
    });
  }

  onPartnerSearchTermChange(searchTerm: string): void {
    // Load partners when search term changes (debounced by the component)
    this.loadAvailablePartners(searchTerm);
  }

  onPartnerSelected(partner: Partner | null): void {
    if (!partner) {
      this.selectedPartner = null;
      this.newConnection.toConnectId = '';
      return;
    }
    this.selectedPartner = partner;
    this.newConnection.toConnectId = partner.id?.toString() || '';
  }

  onPartnerValueSelected(value: any): void {
    // Handle when a value (id) is selected
    if (!value) {
      this.selectedPartner = null;
      this.newConnection.toConnectId = '';
    }
  }

  toggleAddConnectionForm(): void {
    this.showAddConnectionForm = !this.showAddConnectionForm;
    if (!this.showAddConnectionForm) {
      this.resetAddConnectionForm();
    }
  }

  resetAddConnectionForm(): void {
    this.newConnection = {
      toConnectId: ''
    };
    this.selectedPartner = null;
    this.filteredPartners = [];
    this.partnersLoaded = false;
    this.currentSearchTerm = '';
    this.addConnectionError = null;
  }

  addConnection(): void {
    if (!this.newConnection.toConnectId) {
      this.addConnectionError = 'Please select a partner to connect';
      return;
    }

    if (!this.partnerId) {
      this.addConnectionError = 'Partner ID not found';
      return;
    }

    this.addConnectionLoading = true;
    this.addConnectionError = null;

    const connectionData: CreateConnectionRequest = {
      toConnectId: parseInt(this.newConnection.toConnectId)
    };

    this.partnersService.addPartnerConnection(this.partnerId, connectionData).subscribe({
      next: (response) => {
        console.log('Connection added successfully:', response);
        this.addConnectionLoading = false;
        this.showAddConnectionForm = false;
        this.resetAddConnectionForm();
        // Reload connections to show the new one
        this.loadPartnerConnections(this.partnerId!);
      },
      error: (error) => {
        console.error('Error adding connection:', error);
        if (error.status === 400) {
          this.addConnectionError = 'Invalid connection data';
        } else if (error.status === 409) {
          this.addConnectionError = 'Connection already exists';
        } else {
          this.addConnectionError = 'Failed to add connection. Please try again.';
        }
        this.addConnectionLoading = false;
      }
    });
  }

  getConnectionStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'active': return 'status-active';
      case 'pending': return 'status-pending';
      case 'inactive': return 'status-inactive';
      default: return 'status-unknown';
    }
  }

  // Tab management methods
  setActiveTab(tab: 'basic' | 'connections' | 'users' | 'cases'): void {
    this.activeTab = tab;
  }

  // Get the appropriate tab name based on partner type
  getThirdTabName(): string {
    return this.partner?.type === PartnerType.COURIER ? 'shipments' : 'cases';
  }

  // Check if we should show cases or shipments
  shouldShowCases(): boolean {
    return this.partner?.type !== PartnerType.COURIER;
  }

  shouldShowShipments(): boolean {
    return this.partner?.type === PartnerType.COURIER;
  }

  // Load partner users (tenants only — see the isTenant gate in loadPartnerDetails)
  /**
   * The invitations this tenant is waiting on.
   *
   * Shown in the same table as its users, marked rather than separated: an invitation holds one of
   * the tenant's seats, and the place somebody looks for a person they think was added is the
   * user list.
   */
  invitations: InvitationDTO[] = [];
  invitationBusyId: number | null = null;

  get pendingInvitations(): InvitationDTO[] {
    return this.invitations.filter(invitation => invitation.status === 'PENDING');
  }

  loadPartnerInvitations(tenantId: string | number): void {
    this.partnersService.getPartnerInvitations(tenantId).subscribe({
      next: (response) => this.invitations = response.data ?? [],
      // Never at the cost of the user list, which is what the tab is for.
      error: (error) => console.error('Error loading invitations:', error)
    });
  }

  resendInvitation(invitation: InvitationDTO): void {
    if (this.partner?.id == null) {
      return;
    }
    this.invitationBusyId = invitation.id;
    this.partnersService.resendPartnerInvitation(this.partner.id, invitation.id).subscribe({
      next: () => {
        this.invitationBusyId = null;
        this.loadPartnerInvitations(this.partner!.id!);
        this.snackBar.open('Invitation sent again', 'Close', { duration: 4000, panelClass: ['success-snackbar'] });
      },
      error: () => {
        this.invitationBusyId = null;
        this.snackBar.open('Could not send it again', 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
      }
    });
  }

  withdrawInvitation(invitation: InvitationDTO): void {
    if (this.partner?.id == null) {
      return;
    }
    this.invitationBusyId = invitation.id;
    this.partnersService.withdrawPartnerInvitation(this.partner.id, invitation.id).subscribe({
      next: () => {
        this.invitationBusyId = null;
        this.loadPartnerInvitations(this.partner!.id!);
        // The seat it held is free again, and that count is on this tab.
        this.loadPartnerUsers(this.partner!.id!);
      },
      error: () => {
        this.invitationBusyId = null;
        this.snackBar.open('Could not withdraw it', 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
      }
    });
  }

  loadPartnerUsers(tenantId: string | number): void {
    this.usersLoading = true;
    this.usersError = null;
    this.usersNotTenant = false;
    this.loadPartnerInvitations(tenantId);

    this.partnersService.getPartnerUsers(tenantId).subscribe({
      next: (response: any) => {
        // The endpoint returns { data, size }; the other shapes are tolerated defensively.
        if (response && response.data && Array.isArray(response.data)) {
          this.users = response.data;
        } else if (Array.isArray(response)) {
          this.users = response;
        } else if (response && response.content && Array.isArray(response.content)) {
          this.users = response.content;
        } else {
          this.users = [];
        }
        this.usersLoading = false;
      },
      error: (error) => {
        console.error('Error loading partner users:', error);
        this.usersError = 'Failed to load partner users';
        this.usersLoading = false;
      }
    });
  }

  // ---- Add user ----

  toggleAddUserForm(): void {
    this.showAddUserForm = !this.showAddUserForm;
    if (!this.showAddUserForm) {
      this.resetNewUserForm();
    }
  }

  resetNewUserForm(): void {
    this.newUser = { firstName: '', lastName: '', email: '', role: '' };
    this.addUserError = null;
  }

  createUser(attachExisting = false): void {
    const firstName = this.newUser.firstName.trim();
    const lastName = this.newUser.lastName.trim();
    const email = this.newUser.email.trim();

    if (!firstName) {
      this.addUserError = 'First name is required';
      return;
    }
    if (!lastName) {
      this.addUserError = 'Last name is required';
      return;
    }
    // The email doubles as the Keycloak username, so a typo here is not something the user can
    // fix themselves later.
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.addUserError = 'A valid email address is required';
      return;
    }
    if (!this.newUser.role || !this.assignableRoles.includes(this.newUser.role)) {
      this.addUserError = 'Please choose a role';
      return;
    }
    if (this.partner?.id == null) {
      this.addUserError = 'Tenant ID not found';
      return;
    }

    this.addUserLoading = true;
    this.addUserError = null;

    const request: AdminUserCreateRequest = {
      tenantId: this.partner.id,
      firstName,
      lastName,
      email,
      role: this.newUser.role,
      ...(attachExisting ? { attachExisting: true } : {})
    };

    this.partnersService.createPartnerUser(request).subscribe({
      next: (response) => {
        this.addUserLoading = false;
        this.showAddUserForm = false;
        this.existingAccountEmail = null;
        this.resetNewUserForm();
        if (response.existingAccount || !response.temporaryPassword) {
          // Nothing to hand over: they already had an account and keep its password.
          this.addedExistingAccount = email;
        } else {
          // Only chance to show this — it is not stored and cannot be fetched again.
          this.createdUserCredentials = { email, password: response.temporaryPassword };
        }
        this.loadPartnerUsers(this.partner!.id!);
      },
      error: (error) => {
        console.error('Error creating user:', error);
        // The API answers with two error bodies: ConflictException and BadRequestException carry
        // `exceptionMessage`, the rest carry `message`. Reading only one of them sent every 409
        // to the generic branch, so "that address already has an account" - the case this flow
        // exists for - arrived as a flat failure.
        const body = error.error ?? {};
        const message = typeof body.exceptionMessage === 'string' ? body.exceptionMessage
          : (typeof body.message === 'string' ? body.message : '');
        if (message.includes('USER_ALREADY_EXISTS')) {
          // A question rather than a failure: the email belongs to somebody, and an admin is
          // exactly who can decide whether it is the person they meant.
          this.existingAccountEmail = email;
          this.addUserError = null;
        } else if (message.includes('USER_ALREADY_MEMBER')) {
          this.addUserError = 'That account already belongs to this tenant.';
        } else if (message.includes('USER_ACCOUNT_UNVERIFIED')) {
          this.addUserError = 'That account has not verified its email address, so it cannot be invited yet.';
        } else if (error.status === 409) {
          this.addUserError = 'A user with that email already exists.';
        } else if (error.status === 400) {
          this.addUserError = message || 'Invalid user details.';
        } else if (error.status === 404) {
          this.addUserError = 'This tenant no longer exists.';
        } else {
          this.addUserError = 'Failed to create user. Please try again.';
        }
        this.addUserLoading = false;
      }
    });
  }

  copyTemporaryPassword(): void {
    if (!this.createdUserCredentials) {
      return;
    }

    navigator.clipboard.writeText(this.createdUserCredentials.password).then(
      () => this.snackBar.open('Temporary password copied to clipboard', 'Close', {
        duration: 4000,
        panelClass: ['success-snackbar']
      }),
      () => this.snackBar.open('Could not copy — select the password and copy it manually', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      })
    );
  }

  dismissTemporaryPassword(): void {
    this.createdUserCredentials = null;
  }

  /** "Yes, that is the person" — re-sends the same request with the go-ahead. */
  confirmAddExistingAccount(): void {
    this.createUser(true);
  }

  cancelAddExistingAccount(): void {
    this.existingAccountEmail = null;
  }

  dismissAddedExistingAccount(): void {
    this.addedExistingAccount = null;
  }

  // ---- Users tab formatting ----
  // Thin delegates to src/app/utils/user-display.ts, which the Usage page shares. The method
  // names stay as they were so the template needs no changes.

  getUserRoleLabel(role?: UserRole | string | null): string {
    return userRoleLabel(role);
  }

  getUserRoleClass(role?: UserRole | string | null): string {
    return userRoleClass(role);
  }

  /**
   * How many of this tenant's users the Keycloak import has not reached.
   *
   * Worth a line of its own because the roles look the same either way: a member with no row
   * behaves exactly as they do today, right up until the fallback is switched off, and then they
   * have no access at all.
   */
  get usersWithoutMembershipRow(): number {
    return this.users.filter(user => !user.membershipRole).length;
  }

  getUserStatusLabel(user: AdminUserDTO): string {
    return userStatusLabel(user.enabled, user.membershipActive);
  }

  getUserStatusClass(user: AdminUserDTO): string {
    return userStatusClass(user.enabled, user.membershipActive);
  }

  formatRelative(iso?: string | null): string {
    return formatRelative(iso);
  }

  formatAbsolute(iso?: string | null): string {
    return formatAbsolute(iso);
  }

  lastActiveTooltip(user: AdminUserDTO): string {
    return lastActiveTooltip(user);
  }

  // Load partner cases
  loadPartnerCases(page: number = 0): void {
    if (!this.partner || !this.partnerId) {
      return;
    }

    this.casesLoading = true;
    this.casesError = null;
    this.casesPage = page;

    // Determine filter based on partner type
    const queryParams: CasesQueryParams = {
      page: page,
      size: this.casesSize,
      sort: [] // Empty sort as specified
    };

    if (this.partner.type === PartnerType.DENTIST) {
      queryParams.doctorId = parseInt(this.partnerId);
    } else if (this.partner.type === PartnerType.TECH) {
      queryParams.techId = parseInt(this.partnerId);
    } else {
      // For other partner types, we might not have cases
      this.cases = [];
      this.casesLoading = false;
      return;
    }

    this.casesService.getAllCases(queryParams).subscribe({
      next: (response: any) => {
        // Handle PaginatedCollection<Case> response
        if (response && response.data && Array.isArray(response.data)) {
          this.cases = response.data;
          this.casesTotalElements = response.total || 0;
          this.casesTotalPages = Math.ceil((response.total || 0) / this.casesSize);
        } else if (Array.isArray(response)) {
          this.cases = response;
          this.casesTotalElements = response.length;
          this.casesTotalPages = 1;
        } else {
          this.cases = [];
          this.casesTotalElements = 0;
          this.casesTotalPages = 0;
        }
        this.casesLoading = false;
      },
      error: (error) => {
        console.error('Error loading partner cases:', error);
        this.casesError = 'Failed to load partner cases';
        this.casesLoading = false;
      }
    });
  }

  // Cases pagination methods
  goToCasesPage(page: number): void {
    if (page >= 0 && page < this.casesTotalPages) {
      this.loadPartnerCases(page);
    }
  }

  nextCasesPage(): void {
    if (this.casesPage < this.casesTotalPages - 1) {
      this.goToCasesPage(this.casesPage + 1);
    }
  }

  previousCasesPage(): void {
    if (this.casesPage > 0) {
      this.goToCasesPage(this.casesPage - 1);
    }
  }

  // Get case status CSS class
  getCaseStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'completed': return 'status-completed';
      case 'in-progress': return 'status-in-progress';
      case 'pending': return 'status-pending';
      case 'cancelled': return 'status-cancelled';
      default: return 'status-unknown';
    }
  }

  // Get doctor name from case
  getDoctorName(caseItem: Case): string {
    return caseItem.doctor?.contact?.name || 'Unknown Doctor';
  }

  // Get tech name from case
  getTechName(caseItem: Case): string {
    return caseItem.tech?.contact?.name || 'Unknown Tech';
  }

  // Helper method for pagination display
  getCasesEndIndex(): number {
    return Math.min((this.casesPage + 1) * this.casesSize, this.casesTotalElements);
  }

  // Load partner shipments
  loadPartnerShipments(page: number = 0): void {
    if (!this.partner || !this.partnerId) {
      return;
    }

    this.shipmentsLoading = true;
    this.shipmentsError = null;
    this.shipmentsPage = page;

    const queryParams: ShipmentsQueryParams = {
      page: page,
      size: this.shipmentsSize,
      sort: [], // Empty sort as specified
      partnerId: parseInt(this.partnerId)
    };

    this.partnersService.getPartnerShipments(queryParams).subscribe({
      next: (response: any) => {
        // Handle PaginatedCollection<Shipment> response
        if (response && response.data && Array.isArray(response.data)) {
          this.shipments = response.data;
          this.shipmentsTotalElements = response.total || 0;
          this.shipmentsTotalPages = Math.ceil((response.total || 0) / this.shipmentsSize);
        } else if (Array.isArray(response)) {
          this.shipments = response;
          this.shipmentsTotalElements = response.length;
          this.shipmentsTotalPages = 1;
        } else if (response && response.content && Array.isArray(response.content)) {
          this.shipments = response.content;
          this.shipmentsTotalElements = response.totalElements || 0;
          this.shipmentsTotalPages = response.totalPages || 0;
        } else {
          this.shipments = [];
          this.shipmentsTotalElements = 0;
          this.shipmentsTotalPages = 0;
        }
        this.shipmentsLoading = false;
      },
      error: (error) => {
        console.error('Error loading partner shipments:', error);
        this.shipmentsError = 'Failed to load partner shipments';
        this.shipmentsLoading = false;
      }
    });
  }

  // Shipments pagination methods
  goToShipmentsPage(page: number): void {
    if (page >= 0 && page < this.shipmentsTotalPages) {
      this.loadPartnerShipments(page);
    }
  }

  nextShipmentsPage(): void {
    if (this.shipmentsPage < this.shipmentsTotalPages - 1) {
      this.goToShipmentsPage(this.shipmentsPage + 1);
    }
  }

  previousShipmentsPage(): void {
    if (this.shipmentsPage > 0) {
      this.goToShipmentsPage(this.shipmentsPage - 1);
    }
  }

  // Get shipment status CSS class
  getShipmentStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'delivered': return 'status-delivered';
      case 'in-transit': return 'status-in-transit';
      case 'pending': return 'status-pending';
      case 'cancelled': return 'status-cancelled';
      default: return 'status-unknown';
    }
  }

  // Helper method for shipments pagination display
  getShipmentsEndIndex(): number {
    return Math.min((this.shipmentsPage + 1) * this.shipmentsSize, this.shipmentsTotalElements);
  }

  viewCaseDetails(caseItem: Case): void {
    if (caseItem.id) {
      this.router.navigate(['/cases', caseItem.id]);
    }
  }

  askDeleteCase(caseItem: Case): void {
    if (!caseItem.id) return;

    this.pendingDelete = caseItem;
  }

  confirmDeleteCase(): void {
    const caseItem = this.pendingDelete;

    if (!caseItem?.id) return;

    this.deletingCase = true;

    this.casesService.deleteCase(caseItem.id).subscribe({
      next: () => {
        this.deletingCase = false;
        this.pendingDelete = null;
        this.loadPartnerCases(this.casesPage);
      },
      error: (error) => {
        console.error('Error deleting case:', error);
        this.deletingCase = false;
        this.pendingDelete = null;
        this.snackBar.open('Failed to delete the case', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom'
        });
      }
    });
  }

  ngOnDestroy(): void {
    // Clean up blob URL to prevent memory leaks
    if (this.logoBlobUrl) {
      URL.revokeObjectURL(this.logoBlobUrl);
    }
  }
}
